import { useEffect, useState } from 'react';
import { ActivityIndicator, Alert, Platform, Pressable, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { useAuth } from '../context/AuthContext';
import { ApiError } from '../api/client';
import {
  getMyPlatformQuests,
  getPlatformQuestProgress,
  PlatformQuestMineEntry,
  PlatformQuestProgressGroup,
  submitAppStoreRatingProof,
} from '../api/platformQuests';
import { openExternalUrl } from '../utils/openUrl';
import { GradientCard } from './ui';
import { fonts, gradients } from '../theme';

// Android isn't on Play Store yet (still TestFlight/direct-APK testing) —
// this quest is effectively iOS-only until that changes.
const STORE_URL =
  Platform.OS === 'android'
    ? 'https://play.google.com/store/apps/details?id=vn.com.loot'
    : 'https://apps.apple.com/app/id6798160983';

/** Low-cost, capped platform quest: rate the app 5 stars on the store, upload
 * a screenshot as proof, admin approves manually (no automated way to verify
 * a store rating actually happened). Hides once approved, or once the
 * platform-wide cap is full for a user who never attempted it. */
export function AppRatingQuestCard() {
  const { token } = useAuth();
  const [mine, setMine] = useState<PlatformQuestMineEntry | null | undefined>(undefined);
  const [progress, setProgress] = useState<PlatformQuestProgressGroup | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!token) return;
    try {
      const [mineRes, progressRes] = await Promise.all([
        getMyPlatformQuests(token),
        getPlatformQuestProgress(token),
      ]);
      setMine(mineRes.APP_STORE_RATING ?? null);
      setProgress(progressRes.APP_STORE_RATING);
    } catch {
      // Bonus card — fail silently, just don't render progress details.
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (mine === undefined) return null; // still loading, avoid a flash of the wrong state
  if (mine?.status === 'APPROVED') return null;
  if (!mine && progress?.full) return null;

  const onSubmit = async () => {
    if (!token) return;
    setError(null);
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      setError('Chưa được cấp quyền thư viện ảnh — vào Cài đặt để cấp quyền rồi thử lại.');
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
    });
    if (result.canceled || !result.assets[0]) return;
    setSubmitting(true);
    try {
      await submitAppStoreRatingProof(token, result.assets[0].uri);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Gửi minh chứng thất bại');
    } finally {
      setSubmitting(false);
    }
  };

  const onOpenStore = () => {
    Alert.alert(
      'Đánh giá 5 sao cho Loot',
      'Mở trang app trên store, cho 5 sao, sau đó quay lại đây và gửi ảnh chụp màn hình đánh giá bạn vừa để lại.',
      [
        { text: 'Để sau', style: 'cancel' },
        { text: 'Mở trang đánh giá', onPress: () => openExternalUrl(STORE_URL) },
      ],
    );
  };

  return (
    <GradientCard colors={gradients.primary} style={{ marginBottom: 16 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <Ionicons name="star" size={14} color="#fff" />
        <Text style={{ color: '#fff', fontFamily: fonts.bold, fontSize: 12, letterSpacing: 0.4 }}>
          NHIỆM VỤ NỀN TẢNG
        </Text>
      </View>
      <Text style={{ fontSize: 16, fontFamily: fonts.bold, color: '#fff' }}>
        Đánh giá 5 sao cho Loot trên store
      </Text>
      <Text style={{ color: 'rgba(255,255,255,0.85)', marginTop: 4, fontSize: 13, lineHeight: 19 }}>
        Mở app store, để lại 5 sao, chụp màn hình gửi lại — admin duyệt trong 1-2 ngày.
        {progress ? ` Còn ${Math.max(0, progress.cap - progress.count)} suất · +${progress.rewardCoins} xu.` : ''}
      </Text>

      {mine?.status === 'PENDING' ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 12 }}>
          <Ionicons name="time-outline" size={16} color="rgba(255,255,255,0.85)" />
          <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontFamily: fonts.semiBold }}>
            Đang chờ duyệt
          </Text>
        </View>
      ) : (
        <>
          {mine?.status === 'REJECTED' && mine.note ? (
            <Text style={{ color: '#fff', fontSize: 12.5, marginTop: 10, fontFamily: fonts.semiBold }}>
              Bị từ chối: {mine.note} — bạn có thể gửi lại.
            </Text>
          ) : null}
          {error ? <Text style={{ color: '#fff', fontSize: 12.5, marginTop: 10, fontFamily: fonts.semiBold }}>{error}</Text> : null}
          <View style={{ flexDirection: 'row', gap: 10, marginTop: 14 }}>
            <Pressable
              onPress={onOpenStore}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 5,
                paddingVertical: 9,
                paddingHorizontal: 12,
                borderRadius: 10,
                backgroundColor: 'rgba(255,255,255,0.16)',
              }}
            >
              <Ionicons name="open-outline" size={14} color="#fff" />
              <Text style={{ color: '#fff', fontFamily: fonts.semiBold, fontSize: 13 }}>Mở store</Text>
            </Pressable>
            <Pressable
              onPress={onSubmit}
              disabled={submitting}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 5,
                paddingVertical: 9,
                paddingHorizontal: 12,
                borderRadius: 10,
                backgroundColor: '#fff',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? (
                <ActivityIndicator size="small" color={gradients.primary[0]} />
              ) : (
                <Ionicons name="camera-outline" size={14} color={gradients.primary[0]} />
              )}
              <Text style={{ color: gradients.primary[0], fontFamily: fonts.semiBold, fontSize: 13 }}>
                {mine?.status === 'REJECTED' ? 'Gửi lại minh chứng' : 'Gửi minh chứng'}
              </Text>
            </Pressable>
          </View>
        </>
      )}
    </GradientCard>
  );
}
